import {Button, Stack, TableCell, TableRow, Tooltip, Typography} from "@mui/material";
import {LicenseRequestType, LicenseType} from "../../../../utils/enums/LicenseRequestType";
import moment from "moment/moment";
import {IconAlertCircle} from "@tabler/icons-react";
import React, {useMemo} from "react";
import {updateLicenseState} from "../../../../utils/functions";
import {toast} from "react-toastify";
import {grey} from "@mui/material/colors";
import {doc, setDoc} from "firebase/firestore";
import {db} from "../../../../config/firebase";
import {v4 as uuid} from "uuid";

export const LicenseRow = ({request, license, nft}) => {
  const isExpired = request.expirationDate < new Date().getTime();

  const sendLicense = async () => {
    const requestUid = uuid();
    const dateCreated = new Date().getTime();
    await setDoc(
      doc(db, "license_requests", requestUid),
      {
        ...request,
        uid: requestUid,
        email: request?.recipientMail,
        recipientMail: request?.email,
        state: LicenseRequestType.PENDING,
        type: LicenseType.SEND_LICENCE,
        createdAt: dateCreated,
        expirationDate: new Date(dateCreated + (7 * 24 * 60 * 60 * 1000)).getTime(),
      }
    )
    toast.success(`License sent to recipient`);
    await onUpdateState(LicenseRequestType.SENDED)
  }

  const onUpdateState = async (state) => {
    if (state) {
      try {
        if (request) {
          await updateLicenseState(request?.uid, state)
        }
      } catch (e) {
        console.log(e);
        toast.error(e?.message || `Failed to ${state?.toLowerCase()?.replaceAll("_", " ")} license request`)
      }
    }
  }

  const actionButtons = useMemo(() => {
    if (request.type === LicenseType.REQUEST_LICENCE) {
      return {
        [LicenseRequestType.PENDING]: (
          <Stack direction={"row"} gap={1}>
            <Button
              size={"small"}
              onClick={() => onUpdateState(LicenseRequestType.DECLINED)} sx={{
              flex: 1, borderRadius: 2, px: 1,
              border: 1, borderColor: "divider",
              bgcolor: grey[100], color: grey[700],
              "&:hover": {
                bgcolor: grey[200], color: grey[800],
              }
            }}
            >
              Decline
            </Button>
            <Button
              color={"secondary"} variant={"contained"} size={"small"}
              onClick={() => onUpdateState(LicenseRequestType.ACCEPTED)}
              sx={{flex: 1, borderRadius: 2, px: 1}}
            >
              Acknowledge
            </Button>
          </Stack>
        ),
        [LicenseRequestType.ACCEPTED]: (
          <Button
            className={"sign-button"} size={"small"}
            onClick={sendLicense}
            sx={{px: 2, borderRadius: 2}}
          >
            Send License
          </Button>
        ),
        [LicenseRequestType.DECLINED]: "You have declined this license request",
        [LicenseRequestType.SENDED]: "You have successfully sent the license",
      }[request.state];
    }
    return {
      [LicenseRequestType.PENDING]: `License request sent successfully.`,
      [LicenseRequestType.ACCEPTED]: `License accepted. Awaiting payment.`,
      [LicenseRequestType.DECLINED]: `The user has declined this license.`,
      [LicenseRequestType.PAID]: `Payment received. Preparing for minting.`,
      [LicenseRequestType.MINTED]: `License successfully minted. Ownership confirmed.`,
      [LicenseRequestType.FAILED]: `An error occurred. Please contact support.`,
    }[request.state];
  }, [request])


  return (
    <TableRow key={request?.uid}>
      <TableCell className={"text-grey"}
                 sx={{fontWeight: 400}}>{request?.type === LicenseType.REQUEST_LICENCE ? request?.email : request?.recipientMail}</TableCell>
      <TableCell className={"text-grey"} sx={{fontWeight: 400}}>
        <Tooltip title={"Date license request is created"}>
          {moment(request.createdAt).format("DD MMM YYYY")}
        </Tooltip>
      </TableCell>
      <TableCell className={"text-grey"} sx={{fontWeight: 400}}>
        <Tooltip title={isExpired ? "License expired" : "Expiration date"}>
          <Stack direction={"row"} gap={1} alignItems={"center"}>
            <IconAlertCircle size={16} className={"text-grey"}/>
            <Typography>{moment(request.expirationDate).format("DD MMM YYYY")}</Typography>
          </Stack>
        </Tooltip>
      </TableCell>
      <TableCell
        className={"text-grey"}
        sx={{
          fontWeight: 400,
          textTransform: "capitalize"
        }}
      >
        {request?.type === LicenseType.REQUEST_LICENCE ? "Requests a license for access." : "Sends a license to a recipient."}
      </TableCell>
      <TableCell className={"text-grey"} sx={{fontWeight: 400, p: 0.5}}>
        {actionButtons}
      </TableCell>
    </TableRow>
  )
}